import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, ChevronDown, ChevronRight, CalendarClock } from "lucide-react";
import { Order } from "@/hooks/useOrders";
import { NotificationSettings } from "@/hooks/useNotificationSettings";
import { OrderActions } from "@/components/OrderActions";
import { OrderIdentity } from "@/components/OrderIdentity";
import { EmptyState } from "@/components/EmptyState";
import { groupOrdersByTimeWindow, isCurrentWindow, isUrgentWindow, isPastWindow, TimeWindow } from "@/utils/timeGrouping";
import type { OrderActionKey } from "@/utils/orderActions";

interface TimeGroupedOrdersViewProps {
    orders: Order[];
    onSendConfirmation: (order: Order) => void | Promise<void>;
    onSendReadyNotification: (order: Order) => void | Promise<void>;
    onViewOffer: (order: Order) => void;
    onSendReviewRequest: (order: Order) => void | Promise<void>;
    notificationSettings?: NotificationSettings | null;
    /** Takes the order because this view renders many; see useSendGuard. */
    isSending: (order: Order, action: OrderActionKey) => boolean;
}

export const TimeGroupedOrdersView = ({
    orders,
    onSendConfirmation,
    onSendReadyNotification,
    onViewOffer,
    onSendReviewRequest,
    notificationSettings,
    isSending,
}: TimeGroupedOrdersViewProps) => {
    const timeWindows = groupOrdersByTimeWindow(orders, 30);

    // Auto-expand current and urgent windows on first render.
    const [expandedWindows, setExpandedWindows] = useState<Set<string>>(() => {
        const autoExpand = new Set<string>();
        timeWindows.forEach(window => {
            if (isCurrentWindow(window) || isUrgentWindow(window)) {
                autoExpand.add(window.label);
            }
        });
        return autoExpand;
    });

    const toggleWindow = (label: string) => {
        setExpandedWindows(prev => {
            const next = new Set(prev);
            if (next.has(label)) {
                next.delete(label);
            } else {
                next.add(label);
            }
            return next;
        });
    };

    // Card surface stays neutral; window state is carried by the border weight and
    // the solid header below, so no tint ever stacks on a tint (WCAG AA).
    const getWindowStyles = (window: TimeWindow) => {
        if (isCurrentWindow(window)) {
            return "border-2 border-status-info/50 bg-card";
        }
        if (isUrgentWindow(window)) {
            return "border-2 border-status-warning/50 bg-card";
        }
        if (isPastWindow(window)) {
            // De-emphasized by a muted surface, never by opacity (which crushed the
            // label below AA in the pre-token layout).
            return "border-border bg-muted/40";
        }
        return "border-border bg-card";
    };

    // Current/urgent headers are a SOLID status fill with the token's own
    // foreground, so the label clears AA. Neutral otherwise.
    const getHeaderStyles = (window: TimeWindow) => {
        if (isCurrentWindow(window)) {
            return "bg-status-info text-status-info-foreground";
        }
        if (isUrgentWindow(window)) {
            return "bg-status-warning text-status-warning-foreground";
        }
        if (isPastWindow(window)) {
            return "bg-muted text-muted-foreground";
        }
        return "bg-muted text-foreground";
    };

    if (timeWindows.length === 0) {
        return (
            <EmptyState
                icon={CalendarClock}
                title="No orders with promise times"
                description="Orders grouped by pickup window will show here once they have a promise time. Switch to List view to see the rest."
            />
        );
    }

    return (
        <div className="space-y-4">
            {timeWindows.map((window) => {
                const isExpanded = expandedWindows.has(window.label);
                const isCurrent = isCurrentWindow(window);
                const isUrgent = isUrgentWindow(window);

                return (
                    <Card key={window.label} className={`overflow-hidden rounded-radius-card shadow-sm ${getWindowStyles(window)}`}>
                        <button
                            onClick={() => toggleWindow(window.label)}
                            className={`w-full px-4 py-3 flex items-center justify-between gap-2 transition-opacity duration-200 hover:opacity-90 ${getHeaderStyles(window)}`}
                            aria-expanded={isExpanded}
                        >
                            <div className="flex min-w-0 items-center gap-2.5">
                                {isExpanded ? (
                                    <ChevronDown className="h-5 w-5 flex-none" aria-hidden="true" />
                                ) : (
                                    <ChevronRight className="h-5 w-5 flex-none" aria-hidden="true" />
                                )}
                                <Clock className="h-5 w-5 flex-none" aria-hidden="true" />
                                <span className="truncate text-base font-semibold tabular-nums">{window.label}</span>
                                {isCurrent && (
                                    <span className="flex-none rounded-full bg-status-info-foreground px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-status-info">
                                        Now
                                    </span>
                                )}
                                {isUrgent && !isCurrent && (
                                    <span className="flex-none rounded-full bg-status-warning-foreground px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-status-warning">
                                        Soon
                                    </span>
                                )}
                            </div>
                            <span className="flex-none rounded-full bg-foreground px-2.5 py-1 text-xs font-semibold text-background tabular-nums">
                                {window.orders.length} {window.orders.length === 1 ? 'order' : 'orders'}
                            </span>
                        </button>

                        {isExpanded && (
                            <CardContent className="space-y-3 p-3 sm:p-4">
                                {window.orders.map((order) => {
                                    const claimed = !!order.offer_claimed;
                                    return (
                                        <div
                                            key={order.order_id}
                                            className={`rounded-lg border p-3 sm:p-4 ${claimed ? 'border-status-success/40 bg-status-success/[0.06]' : 'border-border bg-card'}`}
                                        >
                                            <OrderIdentity order={order} variant="compact" />

                                            <OrderActions
                                                order={order}
                                                onSendConfirmation={onSendConfirmation}
                                                onSendReadyNotification={onSendReadyNotification}
                                                onViewOffer={onViewOffer}
                                                onSendReviewRequest={onSendReviewRequest}
                                                notificationSettings={notificationSettings}
                                                isSending={(action) => isSending(order, action)}
                                                variant="compact"
                                            />
                                        </div>
                                    );
                                })}
                            </CardContent>
                        )}
                    </Card>
                );
            })}
        </div>
    );
};

export default TimeGroupedOrdersView;
